import React, { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Loader2, PenLine, RotateCcw, Upload } from "lucide-react";
import { prepareSignatureImage } from "../utils/signatureImage";

export default function SignaturePad({ strokes, onChange, image, onImageChange, onProcessingChange, disabled, invalid = false }) {
  const canvas = useRef(null);
  const active = useRef(null);
  const fileInput = useRef(null);
  const drawnStrokes = useRef(strokes);
  const generation = useRef(0);
  const [drawing, setDrawing] = useState(false);
  const [mode, setMode] = useState(image ? "image" : "draw");
  const [imported, setImported] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [importError, setImportError] = useState("");
  const paint = (lines) => {
    const element = canvas.current;
    if (!element) return;
    const context = element.getContext("2d");
    context.clearRect(0, 0, element.width, element.height);
    context.lineWidth = 3;
    context.lineCap = "round";
    context.strokeStyle = "#172554";
    lines.forEach((line) => {
      context.beginPath();
      line.forEach((point, index) => context[index ? "lineTo" : "moveTo"](point.x * element.width, point.y * element.height));
      context.stroke();
    });
  };
  useEffect(() => { paint(strokes); }, [strokes, mode]);
  useEffect(() => () => { generation.current += 1; }, []);
  const switchMode = (next) => {
    if (next === mode || disabled || processing) return;
    setImportError("");
    if (next === "image") {
      drawnStrokes.current = strokes;
      onChange([]); onImageChange?.(imported?.dataUrl || null);
    } else {
      onImageChange?.(null); onChange(drawnStrokes.current);
    }
    setMode(next);
  };
  const importFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || disabled || processing) return;
    const current = ++generation.current;
    setProcessing(true); onProcessingChange?.(true); setImportError("");
    try {
      const result = await prepareSignatureImage(file);
      if (current !== generation.current) return;
      setImported(result); onImageChange?.(result.dataUrl);
    } catch (error) {
      if (current === generation.current) setImportError(error.message || "Impossible d'importer cette image.");
    } finally {
      if (current === generation.current) { setProcessing(false); onProcessingChange?.(false); }
    }
  };
  const point = (event) => {
    const rect = canvas.current.getBoundingClientRect();
    return { x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)), y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)) };
  };
  const finish = (event) => {
    if (!active.current || active.current.pointer !== event.pointerId) return;
    const line = active.current.line;
    active.current = null;
    setDrawing(false);
    if (line.length >= 2) onChange([...strokes, line]);
    else paint(strokes);
  };
  return <div>
    <div className="signature-methods" role="group" aria-label="Méthode de signature"><button type="button" aria-pressed={mode === "draw"} disabled={disabled || processing} onClick={() => switchMode("draw")}><PenLine size={14} aria-hidden="true" />Dessiner</button><button type="button" aria-pressed={mode === "image"} disabled={disabled || processing} onClick={() => switchMode("image")}><Upload size={14} aria-hidden="true" />Importer une image</button></div>
    {mode === "image" ? <>
      <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" aria-label="Fichier image de signature" hidden disabled={disabled || processing} onChange={importFile} />
      <div className={`signature-pad ${invalid ? "is-invalid" : ""}`}>
        {image ? <div className="signature-import-preview"><img src={image} alt="Signature importée" /></div> : <div className="signature-import-empty"><ImageIcon size={26} aria-hidden="true" /><p>Importez votre signature</p><span>Une image sur fond blanc ou transparent</span></div>}
        <div className="signature-import-actions"><button type="button" className="signature-secondary" disabled={disabled || processing} onClick={() => fileInput.current?.click()}>{processing ? <Loader2 size={14} className="signature-spin" aria-hidden="true" /> : <Upload size={14} aria-hidden="true" />}{processing ? "Préparation de l'image..." : image ? "Remplacer l'image" : "Choisir une image"}</button>{image && <button type="button" className="signature-text-button" disabled={disabled || processing} onClick={() => { setImported(null); onImageChange?.(null); setImportError(""); }}>Supprimer l'image</button>}</div>
        {image && imported?.name && <p className="signature-import-name" title={imported.name}>{imported.name}</p>}
      </div>
      <p className="signature-field-help">PNG, JPEG ou WebP, 5 Mo maximum. La signature sera ajustée à la case du devis.</p>
      {importError && <p role="alert" className="signature-field-error">{importError}</p>}
    </> : <><div className={`signature-pad ${invalid ? "is-invalid" : ""}`}><div className="signature-pad-surface">
    {!strokes.length && !drawing && <div className="signature-pad-placeholder" aria-hidden="true"><PenLine size={21} /><span>Signez ici</span></div>}
    <canvas ref={canvas} width={900} height={300} aria-label="Zone de signature manuscrite" aria-describedby="signature-pad-help"
      aria-disabled={disabled} style={{ touchAction: "none" }}
      onPointerDown={(event) => {
        if (disabled || active.current || strokes.length >= 100 || event.button !== 0) return;
        canvas.current.setPointerCapture(event.pointerId);
        active.current = { pointer: event.pointerId, line: [point(event)] };
        setDrawing(true);
      }}
      onPointerMove={(event) => {
        if (!active.current || disabled || active.current.pointer !== event.pointerId) return;
        if (strokes.reduce((count, stroke) => count + stroke.length, 0) + active.current.line.length >= 6000) return;
        active.current.line.push(point(event));
        paint([...strokes, active.current.line]);
      }} onPointerUp={finish} onPointerCancel={finish} onLostPointerCapture={finish} />
    </div><div className="signature-pad-toolbar"><span>Au doigt ou à la souris</span><button type="button" disabled={disabled || !strokes.length} onClick={() => { active.current = null; setDrawing(false); onChange([]); }}><RotateCcw size={12} aria-hidden="true" />Effacer la signature</button></div></div>
    <p id="signature-pad-help" className="signature-field-help">Votre signature sera placée dans la case prévue sur le devis.</p>
    </>}
  </div>;
}
